import {
  FETCH_ROLES_LIST,
  FETCH_ROLEINFO_INIT,
  FETCH_ROLE_DETAIL,
  TOAST,
  UPDATE_ROLE,
  CREATE_ROLE,
  DELETE_ROLE,
  INIT_CREATE_ROLE_SCREEN,
  ROLE_SAVE_TABLE_STATE,
  FETCH_ROLE_MENU_ACCESS,
  FETCH_ALL_MENUS,
  SET_DELETE_CONFIRMATION_POPUP_VISIBILITY,
} from "../../../types/Systems/Role.type";

const formFieldValueMap = {
  id: null,
  title: "",
  status: "1",
  createdTime: "",
  modifiedTime: "",
  modifiedBy: "",
  createdBy: "",
  menuAccess: [],
};

const INITIAL_STATE = {
  roleListView: [],
  allMenusList: [],
  isLoading: false,
  error: null,
  formFieldValueMap,
  isRoleDetailSuccess: false,
  isRoleDetailError: false,
  isRoleCreateSuccess: false,
  isRoleCreateError: false,
  isUpdateRoleSuccess: false,
  isUpdateRoleError: false,
  isRoleMenuAccessSuccess: false,
  isRoleMenuAccessError: false,
  isAllMenusSuccess: false,
  isAllMenusError: false,
  savedTableState: null,
};

const transformRoleData = (content) => {
  const rows = content.map((role) => ({
    id: role.id,
    title: role.title || "N/A",
    status: role.status || "N/A",
    createdBy: role?.createdBy || "N/A",
    createdTime: role?.createdTime || "N/A",
    modifiedTime: role.modifiedTime || "N/A",
    modifiedBy: role.modifiedBy || "N/A",
  }));

  const columns = [
    {
      colLabel: "Column Id",
      dataKey: "id",
      extraProps: {
        isSortable: false,
        isDateField: false,
        shouldFilter: false,
        hidden: true,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
    {
      colLabel: "Role Name",
      dataKey: "title",
      extraProps: {
        isSortable: true,
        isDateField: false,
        shouldFilter: true,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
        sortField: "name",
      },
    },
    {
      colLabel: "Status",
      dataKey: "status",
      extraProps: {
        isSortable: true,
        isDateField: false,
        shouldFilter: true,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
    {
      colLabel: "Created By",
      dataKey: "createdBy",
      extraProps: {
        isSortable: true,
        isDateField: false,
        shouldFilter: true,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
    {
      colLabel: "Created Time",
      dataKey: "createdTime",
      extraProps: {
        isSortable: true,
        isDateField: true,
        shouldFilter: true,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
    {
      colLabel: "Edit By",
      dataKey: "modifiedBy",
      extraProps: {
        isSortable: true,
        isDateField: false,
        shouldFilter: true,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
    {
      colLabel: "Edit Time",
      dataKey: "modifiedTime",
      extraProps: {
        isSortable: true,
        isDateField: true,
        shouldFilter: true,
        hidden: false,
        showFilterMenu: false,
        filterPlaceholder: "Search...",
      },
    },
  ];

  return { datas: { columns, rows } };
};

export const roleReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case FETCH_ROLEINFO_INIT:
      // Preserve savedTableState when reinitializing to restore filters on back navigation
      return { ...INITIAL_STATE, isLoadingPage: false, savedTableState: state.savedTableState };

    case FETCH_ROLES_LIST.START:
    case FETCH_ROLE_DETAIL.START:
    case FETCH_ROLE_MENU_ACCESS.START:
    case UPDATE_ROLE.START:
    case CREATE_ROLE.START:
    case FETCH_ALL_MENUS.START:
    case DELETE_ROLE.START:
      return { ...state, isLoading: true };

    case INIT_CREATE_ROLE_SCREEN: {
      // Preserve savedTableState when navigating to create/edit screen
      return { ...INITIAL_STATE, savedTableState: state.savedTableState };
    }

    case CREATE_ROLE.SUCCESS: {
      return {
        ...state,
        isLoading: false,
        isRoleCreateSuccess: true,
        isRoleCreateError: false,
      };
    }

    case CREATE_ROLE.ERROR: {
      return {
        ...state,
        isLoading: false,
        error: action.payload?.message || action.payload?.error || "Role creation failed",
        isRoleCreateSuccess: false,
        isRoleCreateError: true,
      };
    }

    case FETCH_ROLES_LIST.SUCCESS: {
      const { page, data } = action.payload;

      let resetState = {
        roleListView: [],
        paginationInfo: null,
        error: null,
        isLoading: false,
        isLoadingPage: false,
        isPageLevelError: false,
        isCreateRoleSuccess: false,
        isCreateRoleError: false,
        // Preserve savedTableState
        savedTableState: state.savedTableState,
      };

      if (!data) {
        return resetState;
      }

      const { number, size, totalElements, totalPages } = page;
      const { datas } = transformRoleData(data);

      return {
        ...resetState,
        roleListView: datas,
        paginationInfo: {
          pageSize: size,
          totalRecords: totalElements,
          totalPages: totalPages,
          pageNumber: number,
        },
      };
    }

    case FETCH_ROLES_LIST.ERROR: {
      const { error } = action.payload;
      return {
        ...state,
        roleListView: [],
        isLoading: false,
        isLoadingPage: false,
        error,
        isPageLevelError: true,
      };
    }

    case FETCH_ROLE_DETAIL.SUCCESS: {
      const payload = action.payload || {};

      const temp = { ...formFieldValueMap };
      Object.keys(temp).forEach((k) => {
        if (payload.hasOwnProperty(k)) temp[k] = payload[k];
      });

      return {
        ...state,
        isLoading: false,
        formFieldValueMap: temp,
        isRoleDetailSuccess: true,
      };
    }

    case FETCH_ROLE_DETAIL.ERROR: {
      const { error } = action.payload;
      return {
        ...state,
        isLoading: false,
        error,
        isRoleDetailSuccess: false,
        isRoleDetailError: true,
      };
    }

    case FETCH_ROLE_MENU_ACCESS.SUCCESS: {
      const menuAccess = action.payload || [];
      return {
        ...state,
        isLoading: false,
        formFieldValueMap: {
          ...state.formFieldValueMap,
          menuAccess,
        },
        isRoleMenuAccessSuccess: true,
        isRoleMenuAccessError: false,
      };
    }

    case FETCH_ROLE_MENU_ACCESS.ERROR: {
      return {
        ...state,
        isLoading: false,
        isRoleMenuAccessSuccess: false,
        isRoleMenuAccessError: true,
        error: action.payload?.error || "Failed to fetch role menu access",
      };
    }

    case FETCH_ALL_MENUS.SUCCESS: {
      const storedCompanyId = localStorage.getItem('companyId');
      const menus = (action.payload || []).filter((menu) => {
        // Company module is only visible to company id 1 (super-admin company)
        if (menu.title?.trim().toLowerCase() === 'company') {
          return storedCompanyId === '1';
        }
        return true;
      });
      return {
        ...state,
        isLoading: false,
        allMenusList: menus,
        isAllMenusSuccess: true,
        isAllMenusError: false,
      };
    }

    case FETCH_ALL_MENUS.ERROR: {
      return {
        ...state,
        isLoading: false,
        isAllMenusSuccess: false,
        isAllMenusError: true,
        error: action.payload?.error || "Failed to fetch menus",
      };
    }

    case UPDATE_ROLE.SUCCESS: {
      return {
        ...state,
        isLoading: false,
        isUpdateRoleSuccess: true,
        isUpdateRoleError: false,
      };
    }

    case UPDATE_ROLE.ERROR: {
      const { error } = action?.payload;
      return {
        ...state,
        isLoading: false,
        isUpdateRoleSuccess: false,
        isUpdateRoleError: true,
        error: error || "Update failed",
      };
    }


    case SET_DELETE_CONFIRMATION_POPUP_VISIBILITY: {
      return {
        ...state,
        shouldOpenDeleteConfirmationPopup: action.payload,
        isDeleteRoleSuccess: false,
        isDeleteRoleError: false,
      };
    }

    case DELETE_ROLE.SUCCESS: {
      return {
        ...state,
        isLoading: false,
        isDeleteRoleSuccess: true,
        isDeleteRoleError: false,
        shouldOpenDeleteConfirmationPopup: false,
      };
    }

    case DELETE_ROLE.ERROR: {
      return {
        ...state,
        isLoading: false,
        isDeleteRoleSuccess: false,
        isDeleteRoleError: true,
        shouldOpenDeleteConfirmationPopup: false,
        error: action.payload?.error || "Delete failed",
      };
    }

    case TOAST.RESET:
      return {
        ...state,
        isRoleCreateSuccess: false,
        isRoleCreateError: false,
        isUpdateRoleSuccess: false,
        isUpdateRoleError: false,
        isDeleteRoleSuccess: false,
        isDeleteRoleError: false,
      };

    // Save table state (filters, pagination, sort) for restoration
    case ROLE_SAVE_TABLE_STATE: {
      return {
        ...state,
        savedTableState: action.payload,
      };
    }

    default:
      return state;
  }
};
